import { useEffect } from 'react';
import type { FormEvent } from 'react';
import { Badge, CardGrid, EmptyState, LoadingState, ProductCard, SearchBar } from '@beautyburst/ui';
import { AVAILABILITY_LABEL_TEXT } from '@beautyburst/types';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import type { ProductCard as SearchProductCard } from '@beautyburst/customer';
import type { SearchSortOption } from '@beautyburst/types';
import { FilterPanel } from '../filters.tsx';
import type { AppliedFilters, SearchFacet } from '../filters.tsx';
import { customerAnalytics } from '../analytics/client.ts';
import { customerHref } from '../base-path.ts';
import type { CustomerApiClient } from '../api-client.ts';
import { CartAddFeedback, useCartAddFeedback } from '../cart-add-feedback.tsx';

export interface SearchScreenProps {
  query: string;
  result: { cards: readonly SearchProductCard[]; total: number } | null;
  client?: Pick<CustomerApiClient, 'cart'>;
  facets?: readonly SearchFacet[];
  filters?: AppliedFilters;
  sort?: SearchSortOption;
  loading?: boolean;
  suggestions?: readonly string[];
  mediaUrlFor?: (mediaKey: string) => string | null;
  onFiltersChange?: (filters: AppliedFilters) => void;
  onSortChange?: (sort: SearchSortOption) => void;
  onSearch?: (path: string) => void;
  /** Use an h2 when the search results are embedded below another page h1. */
  headingLevel?: 1 | 2;
}

function stockState(status: SearchProductCard['stockStatus']) {
  if (status === 'LOW') return 'LOW' as const;
  if (status === 'CRITICAL') return 'CRITICAL' as const;
  if (status === 'OUT') return 'OUT_OF_STOCK' as const;
  return 'IN_STOCK' as const;
}

/** Renders only an already-composed server label. Null stays absent—there is no UI fallback. */
export function ServerAvailability({ label }: { label: SearchProductCard['availabilityLabel'] }) {
  return label === null ? null : <Badge variant="info">{AVAILABILITY_LABEL_TEXT[label]}</Badge>;
}

function SearchResultCard({
  card,
  client,
  mediaUrlFor,
}: {
  card: SearchProductCard;
  client?: Pick<CustomerApiClient, 'cart'>;
  mediaUrlFor?: (mediaKey: string) => string | null;
}) {
  const { addToBag, dismiss, feedback } = useCartAddFeedback(client);
  return (
    <li>
      <ProductCard
        href={customerHref(`/products/${card.productSlug}`)}
        imageUrl={
          card.image.mediaKey === null || mediaUrlFor === undefined
            ? null
            : mediaUrlFor(card.image.mediaKey)
        }
        imageAlt={card.image.altText}
        brand={card.brand ?? 'Beauty product'}
        name={card.productName}
        variantLabel={card.variantLabel}
        priceCents={card.priceCents}
        saleActive={card.saleActive}
        salePriceCents={card.salePriceCents}
        retailerName={card.ownershipType === 'BEAUTY_BURST_DIRECT' ? null : card.sellerDisplayName}
        storeName={card.storeName}
        stockState={stockState(card.stockStatus)}
        distanceMiles={card.distanceMiles}
        canAddToBag={card.addToBagPermitted}
        addToBagBlockedReason={
          card.addToBagPermitted ? null : 'This item is currently out of stock.'
        }
        onAddToBag={() => void addToBag({ listingId: card.listingId, quantity: 1 })}
        availabilitySlot={<ServerAvailability label={card.availabilityLabel} />}
      />
      <CartAddFeedback feedback={feedback} onDismiss={dismiss} />
    </li>
  );
}

export function SearchScreen({
  query,
  result,
  facets = [],
  filters = {},
  sort = 'RELEVANCE',
  loading = false,
  suggestions = [],
  mediaUrlFor,
  client,
  onFiltersChange = () => {},
  onSortChange = () => {},
  onSearch,
  headingLevel = 1,
}: SearchScreenProps) {
  const Heading = `h${headingLevel}` as 'h1' | 'h2';
  useEffect(() => {
    if (result === null || loading || query.trim() === '') return;
    // The search repository already retains normalized demand terms. Do not put a
    // customer-entered query in the event stream, where it could be personal data.
    void customerAnalytics()
      .track({
        name: 'search.submitted',
        subjectKind: 'SEARCH',
        properties: { resultCount: result.total },
      })
      .catch(() => undefined);
    if (result.total === 0)
      void customerAnalytics()
        .track({
          name: 'search.zero_results',
          subjectKind: 'SEARCH',
          properties: { resultCount: 0 },
        })
        .catch(() => undefined);
  }, [loading, query, result]);
  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextQuery = new FormData(event.currentTarget).get('q');
    const params = new URLSearchParams();
    if (typeof nextQuery === 'string' && nextQuery.trim() !== '') params.set('q', nextQuery.trim());
    onSearch?.(customerHref(`/search${params.size === 0 ? '' : `?${params.toString()}`}`));
  };
  return (
    <section aria-labelledby="search-heading" style={{ display: 'grid', gap: space[5] }}>
      <Heading
        id="search-heading"
        style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary, margin: 0 }}
      >
        Search beauty products
      </Heading>
      <SearchBar action={customerHref('/search')} defaultValue={query} onSubmit={submitSearch} />
      {suggestions.length > 0 ? (
        <section aria-labelledby="suggestions-heading">
          <h2 id="suggestions-heading" style={typeStyle('headingSm')}>
            Suggestions
          </h2>
          <ul>
            {suggestions.map((suggestion) => (
              <li key={suggestion}>{suggestion}</li>
            ))}
          </ul>
        </section>
      ) : null}
      <FilterPanel
        facets={facets}
        value={filters}
        sort={sort}
        onChange={onFiltersChange}
        onSortChange={onSortChange}
      />
      {loading ? <LoadingState label="Loading search results" /> : null}
      {result === null || loading ? null : (
        <>
          <p
            aria-live="polite"
            style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary, margin: 0 }}
          >
            {result.total} {result.total === 1 ? 'result' : 'results'} for “{query}”
          </p>
          <h2 style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary, margin: 0 }}>
            Search results
          </h2>
          {result.cards.length === 0 ? (
            <EmptyState
              title="No matches yet"
              description="Try another search or clear a filter to see available products."
              action={<a href={customerHref('/categories')}>Browse beauty categories</a>}
            />
          ) : (
            <CardGrid label="Search results">
              <>
                {result.cards.map((card) => (
                  <SearchResultCard
                    key={card.listingId}
                    card={card}
                    client={client}
                    mediaUrlFor={mediaUrlFor}
                  />
                ))}
              </>
            </CardGrid>
          )}
        </>
      )}
    </section>
  );
}
